import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { IconButton, MobileStepper, Stack } from '@mui/material';
import PaiTooltip from 'features/ui/base-components/pai-tooltip';
import { useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { LinkedinAccount } from '../linkedin-accounts/linkedin-accounts.types';
import LinkedinAccountAvatar from './linkedin-account-avatar';

interface Props {
  linkedinAccounts: LinkedinAccount[];

  isLinkedinAccountActive(account: LinkedinAccount): boolean;

  handleSelectedLinkedinAccountChange(id: string): () => void;
}

const LinkedinAccountAvatarCarousel: React.FC<Props> = ({ linkedinAccounts, isLinkedinAccountActive, handleSelectedLinkedinAccountChange }) => {
  const { classes } = useStyles();

  const [currentPage, setCurrentPage] = useState(0);

  const ITEMS_PER_PAGE = 4;

  const totalPages = Math.ceil(linkedinAccounts.length / ITEMS_PER_PAGE);

  const handleNext = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    } else {
      setCurrentPage(0);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    } else {
      setCurrentPage(totalPages - 1);
    }
  };

  const getInitials = (account: LinkedinAccount) => {
    const firstInitial = account.firstName ? account.firstName.charAt(0) : '';
    const lastInitial = account.lastName ? account.lastName.charAt(0) : '';
    return `${firstInitial}${lastInitial}`;
  };

  const displayedAccounts = linkedinAccounts.slice(currentPage * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE + ITEMS_PER_PAGE);

  return (
    <Stack direction={'row'} alignItems={'center'} gap={2}>
      <Stack className={classes.avatarStack} direction={'row'} alignItems={'center'}>
        {displayedAccounts.map((account, index) => (
          <PaiTooltip
            key={index}
            title={account.firstName && account.lastName ? `${account.firstName} ${account.lastName}` : account.userEmail}
            placement={'bottom'}
          >
            <span>
              <LinkedinAccountAvatar
                text={getInitials(account)}
                active={isLinkedinAccountActive(account)}
                handleSelectedLinkedinAccountChange={handleSelectedLinkedinAccountChange(account._id)}
              />
            </span>
          </PaiTooltip>
        ))}
      </Stack>

      {linkedinAccounts.length > ITEMS_PER_PAGE ? (
        <MobileStepper
          className={classes.dotStepper}
          variant={'dots'}
          steps={totalPages}
          position={'static'}
          activeStep={currentPage}
          nextButton={
            <IconButton className={classes.iconButton} size={'small'} onClick={handleNext}>
              <ArrowForward />
            </IconButton>
          }
          backButton={
            <IconButton className={classes.iconButton} size={'small'} onClick={handlePrevious}>
              <ArrowBack />
            </IconButton>
          }
          sx={{ borderRadius: '6px' }}
        />
      ) : null}
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  avatarStack: {
    // Overlapping avatar row — every avatar but the first slides onto its predecessor.
    '& > span + span': {
      marginLeft: '-8px',
    },
  },
  dotStepper: {
    padding: '5px',
    gap: '8px',
  },
  iconButton: {
    '& .MuiSvgIcon-root': {
      fontSize: theme.typography.pxToRem(20),
      color: theme.palette.greys.medium,
    },
  },
}));

export default LinkedinAccountAvatarCarousel;
